import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Globe, Shield, Zap, Box, Download, CheckCircle, Star, Maximize2, X, MessageCircle, Copy } from 'lucide-react';
import { getProducts } from '../services/productService';
import { createRazorpayOrder, verifyRazorpayPayment } from '../services/paymentService';
import { API_BASE_URL } from '../services/api';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';

const ProductDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [activeImage, setActiveImage] = useState(null);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const res = await getProducts();
                const found = res.data.find(p => p.slug === slug);
                setProduct(found);
                if (found) {
                    setActiveImage(found.thumbnail_image ? `${API_BASE_URL}${found.thumbnail_image}` : 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=1200');
                }
            } catch (error) {
                console.error("Error fetching product:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();

        // Load Razorpay Script
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        document.body.appendChild(script);
        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, [slug]);

    const handlePayment = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login', { state: { from: window.location.pathname } });
            return;
        }

        try {
            setPaying(true);
            const res = await createRazorpayOrder(product._id);
            const { order } = res;

            const options = {
                key: import.meta.env.VITE_RAZORPAY_KEY_ID,
                amount: order.amount,
                currency: order.currency,
                name: "MehareTech",
                description: `Purchase ${product.product_title}`,
                image: "/logo.png",
                order_id: order.id,
                handler: async function (response) {
                    try {
                        await verifyRazorpayPayment({
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        });
                        alert("Payment successful! Redirecting to your dashboard...");
                        navigate('/dashboard');
                    } catch (error) {
                        console.error("Verification failed:", error);
                        alert("Payment verification failed. Please contact support.");
                    }
                },
                prefill: {
                    name: user?.name || "User",
                    email: user?.email || "customer@meharetech.com",
                    contact: user?.phone || "9999999999",
                },
                theme: {
                    color: "#6366f1",
                },
                modal: {
                    ondismiss: function () {
                        setPaying(false);
                    }
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (error) {
            console.error("Order creation failed:", error);
            alert("Failed to initiate payment. Please try again.");
        } finally {
            setPaying(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#030014] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen bg-[#030014] pt-32 text-center">
                <h1 className="text-white text-3xl font-black mb-6">Product Not Found</h1>
                <Link to="/products" className="px-8 py-3 bg-primary text-white rounded-xl font-bold uppercase tracking-widest text-xs">Back to Products</Link>
            </div>
        );
    }

    const allImages = [
        product.thumbnail_image ? `${API_BASE_URL}${product.thumbnail_image}` : null,
        ...(product.gallery_images || []).map(img => `${API_BASE_URL}${img}`)
    ].filter(Boolean);

    return (
        <div className="min-h-screen bg-[#030014] pt-24 pb-20">
            <SEO
                title={`${product.product_title} | MehareTech`}
                description={product.short_description}
            />

            <div className="container mx-auto px-4 max-w-6xl">
                <Link to="/products" className="inline-flex items-center gap-2 text-white/40 hover:text-primary mb-6 transition-colors uppercase text-[10px] font-black tracking-widest group">
                    <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back to marketplace
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    {/* Left: Images (Compact) */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="lg:col-span-7 space-y-3 md:space-y-4"
                    >
                        <div className="relative aspect-[16/10] rounded-3xl overflow-hidden border border-white/10 bg-white/5 group">
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={activeImage}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    src={activeImage}
                                    alt={product.product_title}
                                    className="w-full h-full object-cover"
                                />
                            </AnimatePresence>
                            <button
                                onClick={() => setIsLightboxOpen(true)}
                                className="absolute bottom-4 right-4 p-3 bg-black/50 backdrop-blur-md rounded-2xl text-white opacity-0 group-hover:opacity-100 transition-opacity border border-white/20"
                            >
                                <Maximize2 size={18} />
                            </button>
                        </div>

                        {allImages.length > 1 && (
                            <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 no-scrollbar">
                                {allImages.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveImage(img)}
                                        className={`relative w-16 h-16 md:w-20 md:h-20 flex-shrink-0 rounded-xl md:rounded-2xl overflow-hidden border-2 transition-all ${activeImage === img ? 'border-primary scale-95' : 'border-white/10 hover:border-white/30'
                                            }`}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Right: Info (Compact Sidebar) */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="lg:col-span-5 space-y-4 md:space-y-6"
                    >
                        <div className="p-5 md:p-8 rounded-[1.5rem] md:rounded-[2.5rem] bg-white/5 border border-white/10 backdrop-blur-sm relative overflow-hidden">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="px-3 py-1 bg-primary/20 text-primary border border-primary/20 rounded-full text-[9px] font-black uppercase tracking-widest">
                                    {product.category_id?.name || 'Digital'}
                                </span>
                                {product.version && (
                                    <span className="text-white/30 text-[9px] font-black tracking-widest">V{product.version}</span>
                                )}
                            </div>

                            <h1 className="text-2xl md:text-3xl font-black text-white mb-2 leading-tight uppercase">
                                {product.product_title}
                            </h1>

                            {/* Ratings */}
                            <div className="flex items-center gap-2 mb-6">
                                <div className="flex gap-0.5">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <Star
                                            key={star}
                                            size={14}
                                            className={star <= (product.rating || 5) ? "fill-yellow-400 text-yellow-400" : "text-white/20"}
                                        />
                                    ))}
                                </div>
                                <span className="text-white/40 text-[10px] font-bold uppercase tracking-wider">
                                    {product.rating || 5}.0 Rating
                                </span>
                            </div>

                            <div className="flex items-center gap-4 mb-6">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-3xl font-black text-white">₹{product.sale_price || product.regular_price}</span>
                                    {product.sale_price && (
                                        <span className="text-sm text-white/20 line-through">₹{product.regular_price}</span>
                                    )}
                                </div>
                                <div className="h-8 w-px bg-white/10"></div>
                                <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest">{product.license_type || 'Single License'}</span>
                            </div>

                            <p className="text-white/50 text-xs md:text-sm leading-relaxed mb-6 md:mb-8 line-clamp-3">
                                {product.short_description}
                            </p>

                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={handlePayment}
                                    disabled={paying}
                                    className="w-full py-4 bg-primary text-white rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-3 hover:shadow-lg hover:shadow-primary/20 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {paying ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <ShoppingCart size={16} />
                                    )}
                                    {paying ? 'Processing...' : 'Get Started Now'}
                                </button>

                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() => {
                                            const text = `Check out ${product.product_title} on MehareTech: ${window.location.href}`;
                                            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                                        }}
                                        className="py-3 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 rounded-xl border border-emerald-500/20 text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all"
                                    >
                                        <MessageCircle size={14} /> WhatsApp
                                    </button>
                                    <button
                                        onClick={() => {
                                            navigator.clipboard.writeText(window.location.href);
                                            setCopied(true);
                                            setTimeout(() => setCopied(false), 2000);
                                        }}
                                        className="py-3 bg-white/5 hover:bg-white/10 text-white/60 rounded-xl border border-white/10 text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all"
                                    >
                                        {copied ? <CheckCircle size={14} className="text-primary" /> : <Copy size={14} />}
                                        {copied ? 'Copied!' : 'Copy Link'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Features List */}
                        <div className="grid grid-cols-1 gap-3">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-white/30 mb-1 px-2">Core Features</h4>
                            {product.features?.map((feature, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-3 bg-white/5 rounded-2xl border border-white/5 text-white/70 text-xs font-medium">
                                    <CheckCircle size={14} className="text-primary flex-shrink-0" />
                                    {feature}
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Full Description (Bottom) */}
                <div className="mt-10 md:mt-16 pt-10 md:pt-16 border-t border-white/10">
                    <div className="max-w-3xl mx-auto">
                        <h3 className="text-white font-black uppercase tracking-[0.2em] text-[10px] md:text-xs mb-6 md:mb-8 flex items-center gap-3">
                            <Box size={16} className="text-primary" /> Detailed Review
                        </h3>
                        <div
                            className="text-white/40 leading-relaxed text-xs md:text-sm space-y-3 md:space-y-4 prose prose-invert prose-p:leading-loose"
                            dangerouslySetInnerHTML={{ __html: product.full_description }}
                        />
                    </div>
                </div>
            </div>

            {/* Lightbox Modal */}
            <AnimatePresence>
                {isLightboxOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4"
                        onClick={() => setIsLightboxOpen(false)}
                    >
                        <button className="absolute top-8 right-8 text-white/60 hover:text-white transition-colors">
                            <X size={32} />
                        </button>
                        <motion.img
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            src={activeImage}
                            alt=""
                            className="max-w-full max-h-full object-contain rounded-3xl"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ProductDetail;
